/* KANON `ankiety-klienta-2026-09-16`, `szkice/kierunek-a.html` — the four BANDS one occurrence's
 * report is drawn as, and nothing else.
 *
 * Promoted out of `pages/client-detail/ui/SurveyPane.module.css` on 17 September 2026, when
 * `/raporty` became the second surface that draws a report. The split is along a real seam rather
 * than by size: what moved is how ONE OCCURRENCE reads — the eyebrows, the three reading tracks,
 * the measurement tiles, the answer blocks and the media frame — and what stayed is the PANE that
 * hosts it on the client detail: its card, its head, its skeleton and the comparison beside it.
 * The two class sets were already disjoint, so nothing here is a copy of anything there.
 *
 * The report is drawn inside the client detail's pane and inside `SurveyReportDialog`'s popup, and
 * neither box is declared here: a band knows its own rhythm and nothing about the surface under it.
 * `--space-26px` is that rhythm, and both hosts set the same gap between the bands.
 *
 * The way one ANSWER is drawn is `SurveyReading.module.css`, which this file does not own either.
 */

/* An eyebrow over its content, divided from the band above it by a hairline. The gap between bands
 * is the pane's own `--space-26px`; the rule is what makes four sections read as four rather than
 * as one long column with headings in it. EVERY band carries one, the first included — the rule
 * under the head is what stops „ODCZYTY" reading as a third line of the sentence above it. */
.band {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  padding-top: var(--space-26px);
  border-top: var(--border-hairline) solid var(--border);
}

/* A band's title in sentence case, 13/600 (owner, 4 November 2026): „Odczyty", „Media", not the
 * tracked uppercase eyebrow it was. */
.bandHead {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  margin: var(--space-0);
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
}

.bandTitle {
  color: var(--text-2);
}

/* „komplet", „7 z 10" — how complete the set under this title is. Quieter than the title beside it
 * through SIZE and WEIGHT, and not uppercased — it is a fact about the data rather than a second
 * heading. `--text-2`, because it is a fact a trainer reads: `--text-3` is the ink for a thing that
 * only has to be there. */
.bandAside {
  font-size: var(--type-label);
  font-weight: var(--fw-regular);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

/* ── ODCZYTY: one reading per ROW ───────────────────────────────────────────── */

/* Owner, 16 September 2026: the band went back to a top-to-bottom list. As tiles the readings wore
 * the full measurement's shape, which is right for ten girths read as a block and wrong for four
 * quantities read one at a time — and on a 1300px card a tile's two-end row put „−0,6 kg" a long way
 * from the „68,4" it is a change in.
 *
 * So a row is three tracks anchored at the card's LEFT edge — `label | value | change` — and the
 * first two are fixed. Fixed is the whole point: every value in the band starts at the same x, so
 * the column reads down as one, and no row can push its own number right because the label above it
 * was short. The list is capped at the reading measure; the BAND still spans the card, which is what
 * keeps the eyebrow and its hairline lined up with the three bands under it. */
.readings {
  display: flex;
  flex-direction: column;
  max-width: var(--w-survey-measure);
  margin: var(--space-0);
}

/* A FLOOR rather than a height: a wrapped label, or a chosen option longer than its track, grows the
 * row instead of being clipped by it. The hairline goes BETWEEN rows — the band already carries one
 * over its eyebrow, and a second one directly under it would read as a box around the title. */
.readingRow {
  display: grid;
  grid-template-columns: var(--w-survey-row-label) var(--w-survey-row-value) auto;
  column-gap: var(--space-3);
  align-items: center;
  min-height: var(--h-survey-reading-row);
  padding: var(--space-2) var(--space-0);
}

.readingRow + .readingRow {
  border-top: var(--border-hairline) solid var(--border);
}

/* The question's short form, in the sentence case the canon's own row uses — NOT the tile eyebrow's
 * uppercase micro type, which exists to fit a name into a 132px track and has nothing to fit here. */
.readingName {
  min-width: 0;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-wrap: pretty;
}

.readingValue {
  min-width: 0;
  margin: var(--space-0);
  font-variant-numeric: tabular-nums;
}

/* Exactly one of two things, because a cell carries exactly one: a signed change on a row with
 * arithmetic behind it, or the previous ANSWER on a row whose value is a word. No ink is declared
 * here — `.versus` brings its own, and the verdict classes come from `SurveyReading.module.css`. */
.readingChange {
  min-width: 0;
  margin: var(--space-0);
}

/* What the previous occurrence answered, on a two-option row. Quieter than the value it stands
 * beside through size and weight — it is what this row is being compared against, and it has to be
 * read to mean anything. */
.readingPrevious {
  overflow: hidden;
  font-size: var(--fs-tag);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── PEŁNY POMIAR: the ten girths, as tiles ─────────────────────────────────── */

/* Ten values of one measurement, read as a block rather than one at a time — the one band on this
 * report a grid is right for. The track FLOOR is a token rather than a column count: five fit the
 * card at 1512 and three at 1280, and `auto-fill` is what makes that the width's decision instead
 * of a media query's. */
.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--w-survey-tile), 1fr));
  gap: var(--space-2);
  margin: var(--space-0);
}

.tile {
  display: flex;
  flex-direction: column;
  gap: var(--space-5px);
  min-width: 0;
  padding: var(--space-2) var(--space-3);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

/* The girth's name. It is the only thing saying WHICH measurement the figure under it is, so it
 * takes the ink of something that must be read; the micro type and the letter-spacing are what make
 * it quiet. */
.tileName {
  overflow: hidden;
  font-size: var(--fs-tag);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: var(--ls-lab);
}

/* The reading and its change, side by side and wrapping together — never justified to the tile's
 * two ends, because the two are read as one phrase. */
.tileValue {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: baseline;
  margin: var(--space-0);
}

/* ── The goal, in ODPOWIEDZI: the same tiles, carrying words as well as figures ───────────────
 * A goal's tiles hold „Waga ciała" and „17 lis 2026" beside „82 kg", so the value is the reading
 * figure's ink and weight with the unit after it, and it may wrap rather than clip: a tile's name
 * is one uppercase word, a value is what the trainer came to read. */
.goalValue {
  min-width: 0;
  font-size: var(--fs-body);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-normal);
  color: var(--text);
  letter-spacing: var(--ls-section);
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

.goalUnit {
  margin-inline-start: var(--space-2px);
  font-size: var(--fs-tag);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* The client's own words for the goal: the last tile, across every track of the grid. */
.goalWide {
  grid-column: 1 / -1;
}

/* Read as prose, in the band's primary ink. It keeps the client's own line breaks and wraps at any
 * length — a word longer than the tile breaks rather than pushing the grid wider. */
.goalDescription {
  margin: var(--space-0);
  white-space: pre-line;
  overflow-wrap: anywhere;
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text);
  text-wrap: pretty;
}

.versus {
  display: inline-flex;
  flex: none;
  align-items: center;
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ── ODPOWIEDZI: the questions answered in words ────────────────────────────── */

/* Owner, 17 September 2026: the band „wygląda mega biednie". It was a stack of blocks — a label, an
 * answer under it, sixteen pixels of air, the next label — so at a glance the band had no structure
 * at all and nothing said where one answer ended and the next began.
 *
 * It is a two-track LIST now, on the same measure as ODCZYTY above it: the question in
 * `--w-survey-row-label` and the answer in the column that starts where every reading's value
 * starts, divided by the same hairline the readings use. Two bands of one report reading down the
 * same two columns is the whole of the rhythm this band was missing, and it costs one grid. */
.answers {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* `align-items: start` rather than `center`: a paragraph of free text is many lines tall and its
 * question belongs beside the FIRST of them, not floating in the middle of the block. The second
 * track is `minmax(0, …)` so it may shrink inside the 880px dialog and still cap at the reading
 * measure in the client detail's much wider pane. */
.answer {
  display: grid;
  grid-template-columns: var(--w-survey-row-label) minmax(0, var(--w-survey-measure));
  column-gap: var(--space-3);
  align-items: start;
  min-width: 0;
  padding: var(--space-3) var(--space-0);
}

/* Between rows only. The band already carries a rule over its own eyebrow, and a second one
 * directly under it would read as a box drawn around the title — `.readingRow`'s note, same rule. */
.answer + .answer {
  border-top: var(--border-hairline) solid var(--border);
}

/* The question, in the same sentence case and the same ink as a reading's name, so the two bands'
 * left columns are one column to the eye. The lock sits inline at the end of the words — see
 * `shared/ui/RequiredMark`, which is what makes it sit ON the line rather than above it. */
.answerLabel {
  min-width: 0;
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-wrap: pretty;
}

/* The answer column: what she said, and under it what she said last time. A column rather than a
 * baseline row, because the plate is a second line of this answer and not a thing beside it. */
.answerBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

/* PRIMARY ink at body size and the relaxed leading — this is the one band of the report that is
 * genuinely read rather than scanned, and it was drawn at the same weight as its own question.
 *
 * `--survey-words-ink` carries that same decision INTO `SurveyReading`, whose own `.text` is
 * `--text-2` for the matrix cell and the comparison column. Owner, 17 September 2026: a written or
 * chosen answer in this band was secondary ink while every number beside it was primary, so the two
 * halves of one band disagreed about how much of it was worth reading. */
.answerValue {
  --survey-words-ink: var(--text);

  min-width: 0;
  font-size: var(--fs-body);
  line-height: var(--lh-relaxed);
  color: var(--text);
  text-wrap: pretty;
}

/* What the client said LAST time, under what she said this time — the canon's own plate. The label
 * is a fixed column so two of these stacked read as one pair rather than as two paragraphs. */
.previous {
  display: flex;
  gap: var(--space-2);
  min-width: 0;
  padding: var(--space-2) var(--space-3);
  margin: var(--space-0);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.previousAt {
  flex: none;
  padding-top: var(--space-1px);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: var(--ls-lab);
}

.previousText {
  min-width: 0;
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* ── MEDIA: one framed band for both ────────────────────────────────────────── */

/* The photographs and the clip are ONE act by the client — she stood in front of a camera — so they
 * share one inset panel rather than floating on the card as two loose strips of image. It takes the
 * card's full width, which is the one place on this report that genuinely wants it. */
.mediaBand {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5);
  align-items: flex-start;
  padding: var(--space-4);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.mediaBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.mediaHead {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  margin: var(--space-0);
}

/* It WRAPS rather than truncating. A clip question's name is the trainer's own wording plus the
 * exercise it is about — „Nagranie: przysiad ze sztangą" — and an ellipsis cuts the exercise off
 * the only line that says which lift is on the screen. */
.mediaName {
  min-width: 0;
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  overflow-wrap: anywhere;
}

/* „3 pozy", „wygasa 14 paź" — the fact beside a band's name, right after it rather than pushed to
 * the far edge of the card. */
.mediaNote {
  flex: none;
  font-size: var(--fs-eyebrow);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The photographs are LARGE here — the pane has the room a 340px dialog rail never did, and four
 * poses stand side by side at a size a change is readable off. */
.panePhotos {
  --survey-photo-w: var(--sz-survey-photo-lg-w);
  --survey-photo-h: var(--sz-survey-photo-lg-h);
}

.paneClip {
  --survey-clip-w: var(--sz-survey-clip-lg-w);
  --survey-clip-h: var(--sz-survey-clip-lg-h);

  max-width: 100%;
}
